import type { Node } from '@xyflow/react';
import {
  ActionNodeData,
  EnrollNodeData,
  MappedActionNode,
  MappedEnrollNode,
  MappedWaitNode,
  MappedWorkflowNode,
  MapperRegistryType,
  WaitNodeData,
  WorkflowNode,
} from './map-canvas-content.types';
import { ACTION_TYPE_MAPPER, createCanvasMapperRegistry } from './map-canvas-content.registries';
import { NodeType } from '../hooks/use-sidebar-control.types';
import {
  AndBlockValues,
  EntityType,
} from '../../workflow-sidebar/sidebar-views/editors/enroll-node/enroll-node.types';

const CANVAS_MAPPER_REGISTRY: MapperRegistryType = createCanvasMapperRegistry({
  mapEnrollNode: (node) => ({
    id: node.id,
    ...mapEnrollNode(node),
  }),
  mapWaitNode: (node) => ({
    id: node.id,
    ...mapWaitNode(node),
  }),
  mapActionNode: (node) => ({
    id: node.id,
    ...mapActionNode(node),
  }),
});

export const mapCanvasContent = ({ nodes }: { nodes: Node[] }): MappedWorkflowNode[] => {
  return nodes.filter(isMappableNode).map((node) => {
    const mapper = CANVAS_MAPPER_REGISTRY[node.type] as (n: typeof node) => MappedWorkflowNode;
    return mapper(node);
  });
};

function mapEnrollNode(node: Node<EnrollNodeData>): Omit<MappedEnrollNode, 'id'> {
  const rules = {
    OR: node.data.orBlocks.map((or) => ({
      AND: or.andBlocks.filter(hasEntityType).map((and) => ({
        key: and.entityType,
        value: and.entityId.map((e) => e.value),
        condition: and.condition,
      })),
    })),
  };

  return { type: NodeType.Enroll, rules };
}

function mapWaitNode(node: Node<WaitNodeData>): Omit<MappedWaitNode, 'id'> {
  return {
    type: NodeType.Wait,
    rules: {
      percentage: Number(node.data.percentage),
    },
  };
}

function mapActionNode(node: Node<ActionNodeData>): Omit<MappedActionNode, 'id'> {
  const mapActionRules = ACTION_TYPE_MAPPER[node.data.actionType];

  return {
    type: NodeType.Action,
    rules: mapActionRules(node),
  };
}

const isMappableNode = (node: Node): node is WorkflowNode => {
  return !!node.type && node.type in CANVAS_MAPPER_REGISTRY;
};

const hasEntityType = (and: AndBlockValues): and is AndBlockValues & { entityType: EntityType } => {
  return Boolean(and.entityType);
};
